import { ElNotification } from "element-plus";
import localforage from "localforage";
import { defineStore } from "pinia";
import {
  HousingModuleRoleName,
  OrgWideHubMessage,
  type ReminderDto,
  ReturnActionCode,
  SysOrgDto,
  SysOrgRoleName,
  UserInfoDto,
} from "~/api";
import { useOrgWideHub } from "~/composables/useOrgWideHub";
import { authStore } from "~/stores/authStore";
import { useReminderStore } from "~/stores/ReminderStore";
import { apiClient } from "~/utils/apiClient";
import {
  resetServerTimeOffset,
  setServerTimeOffset,
} from "~/utils/activityTracker";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

const useHubs = true;

const nullUser: UserInfoDto = {
  sysOrgDict: {},
  returnActions: [],
  superAdmin: false,
  isTempAccount: true,
  defaultSysOrgGuid: "",
  userGuid: "",
  loginName: "",
  email: "",
};

const nullOrg: SysOrgDto = {
  name: "",
  guid: "",
  roles: [],
  groupGuids: [],
  modules: [],
};

const orgWideHub = useOrgWideHub();

const handleOrgWideSendMessage = (message: OrgWideHubMessage) => {
  showError(message.text!);
};

const handleOrgWideNewReminder = (reminder: ReminderDto) => {
  const reminderStore = useReminderStore();
  reminderStore.addFromSignalR(reminder);
  ElNotification({
    title: reminder.title ?? "Reminder",
    message: reminder.message ?? "",
    type: "info",
    duration: 5000,
  });
};

export const useMyInfoStore = defineStore("MyInfoStore", {
  state: (): {
    userInfo: UserInfoDto;
    currentSysOrgGuid: string;
    currentSysOrg: SysOrgDto;
    mySysOrgs: SysOrgDto[];
    hasMultipleOrgs: boolean;
    isSuperAdmin: boolean;
    isLoggedIn: boolean;
    storeLoaded: boolean;
    isRefreshing: boolean;
    lastLoadTime: number;
    hasSetupBasicRole: boolean;
    hasSetupItemsRole: boolean;
    callBacksWhenOrgsLoaded: (() => void)[];

    /** housing specific */
    housingModuleRoles: Record<string, HousingModuleRoleName[]>;
    isAgent: boolean;
    isBulkAgent: boolean;
    isAdmin: boolean;
    isPortal: boolean;
  } => {
    return {
      userInfo: nullUser,
      currentSysOrg: nullOrg,
      currentSysOrgGuid: "",
      mySysOrgs: [],
      isLoggedIn: false,
      isSuperAdmin: false,
      storeLoaded: false,
      isRefreshing: false,
      lastLoadTime: 0,
      hasMultipleOrgs: false,
      hasSetupBasicRole: false,
      hasSetupItemsRole: false,
      callBacksWhenOrgsLoaded: [],

      /** housing specific */
      housingModuleRoles: {},
      isAgent: false,
      isBulkAgent: false,
      isAdmin: false,
      isPortal: false,

      // setting lastLoadTime to 0 forces a refresh on browser page reload. This makes the cache only effective during the same page load.
      // setting to Date.now() will make the cache effective for the duration of the cache time.
    };
  },
  actions: {
    /** Local store for information about this user (or anonymous user if not logged in). This store will cache the data in local storage.
     * The data is refreshed if it is older than 10 minutes, or if forceUpdate is set to true.
     * @example
     * ```ts
     * import usingMyInfoStore from "~/YYC_Housing/Intake/stores/LoginAccountsStore";
     * const loginAccountsStore = useLoginAccountsStore();
     *
     * onMounted(async () => {
     *  await useLoginAccountStore.load();
     * });
     *
     * const list = loginAccountsStore.List; // use the list from the store (no need to await)
     * const accountName = loginAccountsStore.getName("YYC"); // use a store function
     * ```
     * @param {boolean} forceUpdate - Can set to true to force a reload from the server.
     */
    async load(forceUpdate: boolean = false) {
      if (this.isRefreshing) {
        // return a promise that will resolve when the store is done refreshing
        return new Promise<void>((resolve, _) => {
          const checkRefreshing = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(checkRefreshing);
              resolve();
            }
          }, 100); // adjust this if needed
        });
      }

      this.isRefreshing = true; // set this to true as soon as possible to prevent multiple parallel calls

      // set to 10 minutes
      const MaxCacheTime = 10 * 60 * 1000;

      // this is not a sliding expiration, it is a hard expiration. Cache isn't actually gone, but we will refresh it.
      if (Date.now() - this.lastLoadTime > MaxCacheTime) {
        forceUpdate = true;
      }

      if (!forceUpdate && this.userInfo?.userGuid) {
        this.isRefreshing = false;
        return null;
      }

      try {
        if (!forceUpdate) {
          const rawCached = await localforage.getItem(this.$id + "-dto");
          if (rawCached) {
            const cached = JSON.parse(rawCached as string) as UserInfoDto;
            this.userInfo = cached;
            await this.setInfo(this.userInfo);
            this.storeLoaded = true;
            this.isRefreshing = false;
            this.executePendingCallbacks();
            return null;
          }
        }

        // show this message only if we are getting fresh data (keep the console.log)
        console.log("getting fresh", this.$id);

        // check if a shortcode was temporarily stored by the Start page
        // get it, then clear it
        const shortcode = (await localforage.getItem("shortcode")) as string;
        localforage.removeItem("shortcode");

        // get the userInfo from the server
        this.userInfo = authStore.account
          ? await apiClient.userInfo.getMyUserInfo(shortcode)
          : await apiClient.userInfo.getMyUserInfoAnon(shortcode);

        if (this.userInfo.serverTimeUtc) {
          setServerTimeOffset(this.userInfo.serverTimeUtc);
        }

        // cache the result
        localforage.setItem(this.$id + "-dto", JSON.stringify(this.userInfo));

        // has the server told us to do something?
        // if there are any returnActions, prepare them for later use
        for (const action of this.userInfo.returnActions ?? []) {
          if (
            action.returnActionCode === ReturnActionCode.SWITCH_TO_ORG &&
            action.guid
          ) {
            // switch to the org
            sessionStorage.setItem("currentOrgGuid", action.guid);
          }

          if (
            action.returnActionCode === ReturnActionCode.SHOW_WORKFLOW &&
            action.guid
          ) {
            // store the workflow to show, another part of the app will pick it up
            sessionStorage.setItem("workflowToShow", action.guid);
          }

          if (
            action.returnActionCode === ReturnActionCode.SHOW_ERROR &&
            action.message
          ) {
            showError(action.message);
          }
        }

        await this.setInfo(this.userInfo);
        this.executePendingCallbacks();

        return null;
      } catch (ex) {
        this.userInfo = nullUser;
        this.currentSysOrg = nullOrg;
        this.storeLoaded = false;
        this.isRefreshing = false;
        throw ex;
      }
    },
    /** Call this after making a change to the underlying data. The next use of the store will get a fresh copy. */
    clear() {
      resetServerTimeOffset();
      this.userInfo = nullUser;
      this.storeLoaded = false;
      this.isSuperAdmin = false;
      this.currentSysOrg = nullOrg;
      this.currentSysOrgGuid = "";
      this.mySysOrgs = [];
      this.hasMultipleOrgs = false;
      this.hasSetupBasicRole = false;
      this.hasSetupItemsRole = false;
      this.isLoggedIn = false;

      // housing specific
      this.housingModuleRoles = {};
      this.isAgent = false;
      this.isBulkAgent = false;
      this.isAdmin = false;
      this.isPortal = false;

      localforage.removeItem(this.$id + "-dto");
    },
    executePendingCallbacks() {
      // call all the callbacks that were waiting for the orgs to be loaded
      if (this.callBacksWhenOrgsLoaded.length) {
        this.callBacksWhenOrgsLoaded.forEach((callback) => callback());
        this.callBacksWhenOrgsLoaded = [];
      }
    },

    async joinOrgHub() {
      if (!authStore.isAuthenticated) {
        return;
      }

      await orgWideHub.ensure();

      // don't await this, let it run in the background
      orgWideHub.invoke("AddToOrgHub", this.currentSysOrg.guid);

      // join user-specific notification group
      orgWideHub.invoke("JoinUserNotificationGroup");

      // keep handlers from being duplicated when this method is called repeatedly
      orgWideHub.off("SendMessage", handleOrgWideSendMessage);
      orgWideHub.off("NewReminder", handleOrgWideNewReminder);

      // listen for messages from the hub
      await orgWideHub.on("SendMessage", handleOrgWideSendMessage);

      // listen for reminders
      await orgWideHub.on("NewReminder", handleOrgWideNewReminder);
    },
    async setInfo(localUserInfo: UserInfoDto) {
      // expose some flags for the UI to use without having to search roles

      this.isSuperAdmin = localUserInfo.superAdmin ?? false;

      this.isLoggedIn =
        !!localUserInfo.userGuid && localUserInfo.userGuid !== emptyGuid;

      // get the org guids
      const orgGuids = Object.keys(localUserInfo.sysOrgDict);

      // make a list of orgs
      this.mySysOrgs = Object.values(localUserInfo.sysOrgDict);
      this.mySysOrgs.sort((a, b) => a.name.localeCompare(b.name)); // sort by name

      this.hasMultipleOrgs = orgGuids.length > 1;

      let currentOrgGuid =
        sessionStorage.getItem("currentOrgGuid") ??
        localUserInfo.defaultSysOrgGuid ??
        orgGuids[0];

      // if the currentOrgGuid is not in the list of orgs, try case-insensitive match, or default to the first one
      if (!orgGuids.includes(currentOrgGuid)) {
        const matchingGuid = orgGuids.find(
          (g) => g.toLowerCase() === currentOrgGuid?.toLowerCase(),
        );
        currentOrgGuid = matchingGuid ?? orgGuids[0];
      }

      // remember for next time
      sessionStorage.setItem("currentOrgGuid", currentOrgGuid);
      this.currentSysOrgGuid = currentOrgGuid;

      // get the current org for use throughout the app
      this.currentSysOrg = localUserInfo.sysOrgDict[currentOrgGuid];

      this.hasSetupBasicRole =
        this.currentSysOrg?.roles?.includes(SysOrgRoleName.SETUP_BASIC) ??
        false;

      this.hasSetupItemsRole =
        this.currentSysOrg?.roles?.includes(SysOrgRoleName.SETUP_ITEMS) ??
        false;

      this.isPortal =
        this.currentSysOrg?.roles?.includes(SysOrgRoleName.MEMBER) ?? false;

      // housing specific role checks - directly from UserInfoDto
      this.housingModuleRoles = localUserInfo.housingModuleRoles ?? {};
      this.isAgent = currentOrgGuid
        ? this.hasHousingRole(currentOrgGuid, HousingModuleRoleName.AGENT)
        : false;

      this.isBulkAgent = currentOrgGuid
        ? this.hasHousingRole(currentOrgGuid, HousingModuleRoleName.BULK_AGENT)
        : false;

      this.isAdmin =
        this.currentSysOrg?.roles?.includes(SysOrgRoleName.USER_ADMIN) ?? false;

      // join this org's hub (authenticated users only — hubs require MSAL tokens)
      if (useHubs && authStore.isAuthenticated) {
        this.joinOrgHub(); // not awaited, so it can run in the background
      }
      // all done
      this.storeLoaded = true;
      this.isRefreshing = false;
      this.lastLoadTime = Date.now();
    },
    onOrgsLoaded(callback: () => void) {
      // this is called when the orgs are loaded, so we can add routes or do other things that depend on the orgs being loaded
      if (this.storeLoaded) {
        callback();
      } else {
        // add to queue to be called when the orgs are loaded
        this.callBacksWhenOrgsLoaded.push(callback);
      }
    },
    hasHousingRole(sysOrgGuid: string, role: HousingModuleRoleName): boolean {
      return this.housingModuleRoles[sysOrgGuid]?.includes(role) ?? false;
    },
  },
  getters: {
    isGuest: (state) =>
      // should this be if they ONLY have the Guest role?
      state.currentSysOrg?.roles.includes(SysOrgRoleName.GUEST) ?? false,
  },
});
